import { apalumaAnalytics } from '@/router/apaluma-analytics.ts';
import { maintenancePageGuard } from '@/router/maintenance-page-guard.ts';
import { useNmedDocAiUiStore } from '@/stores/nmed/nmed-doc-ai-ui-store.ts';
import { useNmedDocSearchFilterStore } from '@/stores/nmed/nmed-doc-search-filter-store.ts';
import { createRouter, createWebHistory } from 'vue-router';
import { adminPageGuard } from './admin-page-guard';
import { authGuard } from './auth-guard';

/* ---------------------------------------------------------------------------------------------- */

// Test UI routes
const testRoutes = [
   {
      path: '/test/ui',
      name: 'test-ui',
      component: () => import('@/views/test/ui/test-ui-page.vue'),
      meta: {
         requiresAuth: true,
      },
   },
];

/* ---------------------------------------------------------------------------------------------- */

const router = createRouter({
   history: createWebHistory(import.meta.env.BASE_URL),
   routes: [
      {
         path: '/',
         redirect: '/documents',
      },
      {
         path: '/chats',
         name: 'home',
         component: () => import('@/views/landing-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/chats/:sessionId',
         name: 'chat',
         component: () => import('@/views/landing-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/documents',
         name: 'documents',
         component: () => import('@/views/documents-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/map',
         name: 'map',
         component: () => import('@/views/map-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/saved-documents',
         name: 'saved-documents',
         component: () => import('@/views/saved-documents-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/projects',
         name: 'projects',
         component: () => import('@/views/projects-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/projects/:projectId',
         name: 'project',
         component: () => import('@/views/project-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/projects/:projectId/chats',
         name: 'project-chat',
         component: () => import('@/views/project-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/projects/:projectId/chats/:threadId',
         name: 'project-chat-thread',
         component: () => import('@/views/project-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/about',
         name: 'about',
         component: () => import('@/views/about-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/settings',
         name: 'settings',
         component: () => import('@/views/settings-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/learning-library',
         name: 'learning-library',
         component: () => import('@/views/learning-library-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/admin',
         name: 'admin',
         component: () => import('@/views/admin-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/under-maintenance',
         name: 'under-maintenance',
         component: () => import('@/views/maintenance-page.vue'),
      },
      {
         path: '/cookie-policy',
         name: 'cookie-policy',
         component: () => import('@/views/cookie-policy-page.vue'),
      },
      {
         path: '/login',
         name: 'login',
         component: () => import('@/views/auth/login-page.vue'),
      },
      {
         path: '/nmed-login',
         name: 'nmed-login',
         component: () => import('@/views/auth/nmed/nmed-login.vue'),
      },
      {
         path: '/auth/callback',
         name: 'cognito-callback',
         component: () => import('@/views/auth/callbacks/cognito-callback-page.vue'),
      },
      {
         path: '/signup',
         name: 'signup',
         component: () => import('@/views/auth/signup-page.vue'),
      },
      {
         path: '/forgot-password',
         name: 'forgot-password',
         component: () => import('@/views/auth/forgot-password-page.vue'),
      },

      {
         path: '/confirm-signup',
         name: 'confirm-signup',
         component: () => import('@/views/auth/confirm-signup-page.vue'),
      },
      {
         path: '/logout',
         name: 'logout',
         component: () => import('@/views/auth/logout-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },

      ...testRoutes,
   ],
});

router.beforeEach(maintenancePageGuard);
router.beforeEach(authGuard);
router.beforeEach(adminPageGuard);
router.beforeEach(apalumaAnalytics);

// Reset document UI state when navigating to a different route
router.afterEach((to, from) => {
   // Only reset if actually navigating between different routes
   // (not on initial load or same-route navigation)
   if (from.name && to.path !== from.path) {
      const docAiUiStore = useNmedDocAiUiStore();
      const docSearchFilterStore = useNmedDocSearchFilterStore();
      docAiUiStore.closeDocAiWindow();
      docSearchFilterStore.clearAllFilters();
   }
});

export default router;
